<template>
  <div>
    <div ref="trigger">
      <CombinedSearch
        ref="searchInput"
        label="Customer Lookup"
        placeholder="Search…(min. 4 characters)"
        show-clear-button
        :validation="Boolean(validation) ? 'error' : ''"
        :helperText="validation"
        @update:searchQuery="handleTyping"
        @search="handleFormSubmit"
      />
    </div>
    <div
      ref="results"
      class="z-20 hidden bg-white rounded-lg shadow dark:bg-gray-700 w-full"
    >
      <ul class="overflow-auto max-h-96">
        <li v-if="searching" class="p-2">
          <Spinner size="small" />
        </li>
        <li
          v-for="(result, i) in lookupResults"
          :key="result.customer?.id + '-index-' + i"
          class="p-2 hover:bg-blue-50 hover:text-blue-700"
          @click="selectCustomer(result)"
        >
          <div class="flex flex-col">
            <span class="font-medium flex items-center gap-1">
              {{
                result.customer.flattened_name ||
                `${result.customer.first_name} ${result.customer.last_name}`
              }}
              <img
                v-if="result.customer.membership?.icon_url"
                :src="result.customer.membership.icon_url"
                :title="result.customer.membership.membership_program_name"
                :alt="result.customer.membership.membership_program_name"
                width="15"
                height="15"
                class="inline-block"
              />
            </span>
            <span class="text-xs text-gray-500 pointer-events-none">{{
              fullAddress(result.address)
            }}</span>
          </div>
        </li>
        <li v-if="!lookupResults.length && submitted" class="p-2">
          <div class="flex flex-col">
            <span class="text-gray-500 text-xs">No Results</span>
          </div>
        </li>
        <li
          v-if="createCustomerEnabled"
          class="p-2 border-t border-gray-100 hover:bg-blue-50 cursor-pointer"
          data-cy="create-new-customer"
          @click="goToNewCustomer"
        >
          <span class="font-medium text-blue-700">＋ Create New Customer</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DropdownInterface, DropdownOptions } from "flowbite";
import { Dropdown } from "flowbite";
import debounce from "lodash.debounce";
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useFeatureFlag } from "@/lib/useFeatureFlag";

import CombinedSearch from "@/components/Seranova/SearchInput/CombinedSearch.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import { fullAddress } from "@/mixins";
import { userCreatePath } from "@/mixins/path_helpers";
import { Address } from "@/typings/address";
const emit = defineEmits(["update:modelValue"]);
const router = useRouter();
const createCustomerEnabled = useFeatureFlag(
  "create_customer_from_dispatch_ff_enabled"
);
const lookupResults = ref<{ customer: LookupResult; address: Address }[]>([]);
const trigger = ref<HTMLElement | null>(null);
const results = ref<HTMLElement | null>(null);
const store = useStore();
const dropdown = ref<DropdownInterface | null>(null);
const searching = ref(false);
const searchInput = ref(null);
const searchTerm = ref("");
const submitted = ref(false);
defineProps<{ validation: string }>();

export interface LookupResult {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  full_name: string;
  match_score: number;
  membership?: {
    icon_url?: string;
    membership_program_name?: string;
  } | null;
  addresses: {
    id: number;
    matches: { [k: string]: [{ start: number; end: number }] };
    street: string;
  };
  all_addresses?: Address[];
  flattened_name: string;
  has_subs: boolean;
  parent?: null;
  matches: { [k: string]: [{ start: number; end: number }] }[];
}

onMounted(() => {
  const options: DropdownOptions = {
    placement: "bottom",
    triggerType: "click",
    offsetSkidding: 0,
    offsetDistance: 10,
    delay: 300
  };
  dropdown.value = new Dropdown(results.value, trigger.value, options);
});

const executeSearch = debounce(async (query: string) => {
  // If the query is too short, clear results and hide the dropdown
  if (query.length <= 3) {
    lookupResults.value = [];
    searching.value = false;
    dropdown.value?.hide();
    submitted.value = false;
    return;
  }

  searching.value = true;
  try {
    const res = await store.dispatch("lookupCustomer", { terms: query });
    lookupResults.value = res.data.reduce((data, customer: LookupResult) => {
      customer.all_addresses?.forEach((address: Address) => {
        data.push({
          customer,
          address
        });
      });
      return data;
    }, []);
    dropdown.value?.show();
  } catch (error) {
    console.error("Customer lookup failed:", error);
    lookupResults.value = [];
  } finally {
    searching.value = false;
    submitted.value = true;
  }
}, 500);

const handleTyping = (query: string) => {
  executeSearch(query);
  searchTerm.value = query;
  submitted.value = false;
};

const handleFormSubmit = (data: {
  query: string;
  category: string;
  filter: string;
}) => {
  executeSearch.cancel();
  executeSearch(data.query);
};

const selectCustomer = (result: {
  customer: LookupResult;
  address: Address;
}) => {
  emit("update:modelValue", result);
  dropdown.value?.hide();
  searchTerm.value = "";
};

const goToNewCustomer = () => {
  // On scheduler surfaces (which register this global), the customer form
  // returns the user to the dispatch board with the new appointment's
  // drawer open instead of the job page.
  const onSchedulerSurface =
    typeof window.handleSchedulerEventEdit === "function";
  dropdown.value?.hide();
  router.push(
    onSchedulerSurface
      ? `${userCreatePath()}?return_to=schedule`
      : userCreatePath()
  );
};
</script>

<style scoped></style>
